import { Badge, Box, HStack, Switch, Text, VStack } from 'components/ui';
import { useState } from 'react';
import {
  MdCheckBox,
  MdCheckBoxOutlineBlank,
  MdChevronRight,
  MdExpandMore,
  MdFolder,
  MdInsertDriveFile,
  MdPublic,
  MdWeb,
} from 'react-icons/md';
import { KbDocumentDto, KbFolderTreeDto, KbWebSourceDto } from 'types/knowledgeBase';

interface KbTreePickerProps {
  folderTree: KbFolderTreeDto[];
  selectedFolderIds: string[];
  onToggleFolder: (folderId: string) => void;
  /** Root-level (uncategorized) items */
  rootDocuments: KbDocumentDto[];
  rootWebSources: KbWebSourceDto[];
  selectedDocumentIds: string[];
  selectedWebSourceIds: string[];
  onToggleDocument: (docId: string) => void;
  onToggleWebSource: (wsId: string) => void;
}

interface TreeNodeProps {
  folder: KbFolderTreeDto;
  selectedFolderIds: string[];
  onToggleFolder: (folderId: string) => void;
}

const TreeNode = ({ folder, selectedFolderIds, onToggleFolder }: TreeNodeProps) => {
  const isSelected = selectedFolderIds.includes(folder.id);
  const itemCount = folder.documentCount + folder.webSourceCount;

  return (
    <HStack
      gap={1}
      px={2}
      py={1}
      pl={2}
      borderRadius="md"
      minH="30px"
      _hover={{ bg: { base: 'gray.50', _dark: 'gray.750' } }}
      userSelect="none"
    >
      {/* Checkbox */}
      <Box
        color={isSelected ? 'blue.500' : folder.isGlobal ? 'purple.400' : { base: 'gray.400', _dark: 'gray.500' }}
        flexShrink={0}
        cursor={folder.isGlobal ? 'default' : 'pointer'}
        onClick={() => !folder.isGlobal && onToggleFolder(folder.id)}
      >
        {folder.isGlobal || isSelected ? <MdCheckBox size={18} /> : <MdCheckBoxOutlineBlank size={18} />}
      </Box>

      {/* Folder icon */}
      <Box color={{ base: 'yellow.500', _dark: 'yellow.400' }} flexShrink={0}>
        <MdFolder size={15} />
      </Box>

      {/* Name + badges */}
      <Text
        fontSize="sm"
        flex={1}
        overflow="hidden"
        textOverflow="ellipsis"
        whiteSpace="nowrap"
        color={{ base: 'gray.800', _dark: 'gray.200' }}
        cursor={folder.isGlobal ? 'default' : 'pointer'}
        onClick={() => !folder.isGlobal && onToggleFolder(folder.id)}
      >
        {folder.name}
      </Text>

      {folder.isGlobal && (
        <Badge colorPalette="purple" size="sm" variant="subtle" flexShrink={0}>
          <MdPublic size={10} style={{ display: 'inline' }} /> Global
        </Badge>
      )}

      {itemCount > 0 && !folder.isGlobal && (
        <Badge size="sm" variant="subtle" colorPalette="gray" flexShrink={0}>{itemCount}</Badge>
      )}
    </HStack>
  );
};

const KbTreePicker = ({
  folderTree,
  selectedFolderIds,
  onToggleFolder,
  rootDocuments,
  rootWebSources,
  selectedDocumentIds,
  selectedWebSourceIds,
  onToggleDocument,
  onToggleWebSource,
}: KbTreePickerProps) => {
  const [rootExpanded, setRootExpanded] = useState(true);

  const hasRootItems = rootDocuments.length > 0 || rootWebSources.length > 0;

  if (folderTree.length === 0 && !hasRootItems) {
    return (
      <Text fontSize="sm" color={{ base: 'gray.500', _dark: 'gray.400' }}>
        No KB items available. Add documents or web sources in the Knowledge Base page.
      </Text>
    );
  }

  return (
    <Box borderRadius="lg" border="1px solid" borderColor={{ base: 'gray.200', _dark: 'gray.700' }} py={1}>
      {/* Folder tree */}
      {folderTree.map(folder => (
        <TreeNode
          key={folder.id}
          folder={folder}
          selectedFolderIds={selectedFolderIds}
          onToggleFolder={onToggleFolder}
        />
      ))}

      {/* Uncategorized items */}
      {hasRootItems && (
        <Box>
          {/* Uncategorized header */}
          <HStack
            gap={1}
            px={2}
            py={1}
            borderRadius="md"
            minH="30px"
            cursor="pointer"
            _hover={{ bg: { base: 'gray.50', _dark: 'gray.750' } }}
            onClick={() => setRootExpanded(v => !v)}
            borderTop={folderTree.length > 0 ? '1px dashed' : 'none'}
            borderColor={{ base: 'gray.200', _dark: 'gray.600' }}
            mt={folderTree.length > 0 ? 1 : 0}
          >
            <Box w="20px" h="20px" flexShrink={0} display="flex" alignItems="center" justifyContent="center"
              color={{ base: 'gray.400', _dark: 'gray.500' }}>
              {rootExpanded ? <MdExpandMore size={16} /> : <MdChevronRight size={16} />}
            </Box>
            <Box color={{ base: 'gray.400', _dark: 'gray.500' }} flexShrink={0}>
              <MdFolder size={15} />
            </Box>
            <Text fontSize="sm" color={{ base: 'gray.600', _dark: 'gray.400' }} flex={1} fontStyle="italic">
              Uncategorized
            </Text>
            <Badge size="sm" variant="subtle" colorPalette="gray">
              {rootDocuments.length + rootWebSources.length}
            </Badge>
          </HStack>

          {rootExpanded && (
            <VStack align="stretch" gap={0}>
              {rootDocuments.map(doc => {
                const isChecked = selectedDocumentIds.includes(doc.id) || doc.isGlobal;
                return (
                  <HStack
                    key={doc.id}
                    gap={1}
                    px={2}
                    py={1}
                    pl="36px"
                    borderRadius="md"
                    minH="28px"
                    cursor={doc.isGlobal ? 'default' : 'pointer'}
                    _hover={!doc.isGlobal ? { bg: { base: 'gray.50', _dark: 'gray.750' } } : {}}
                    onClick={() => !doc.isGlobal && onToggleDocument(doc.id)}
                    userSelect="none"
                  >
                    <Box color={isChecked ? (doc.isGlobal ? 'purple.400' : 'blue.500') : { base: 'gray.400', _dark: 'gray.500' }}
                      flexShrink={0}>
                      {isChecked ? <MdCheckBox size={16} /> : <MdCheckBoxOutlineBlank size={16} />}
                    </Box>
                    <Box color={{ base: 'blue.400', _dark: 'blue.300' }} flexShrink={0}>
                      <MdInsertDriveFile size={14} />
                    </Box>
                    <Text fontSize="xs" flex={1} overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap"
                      color={{ base: 'gray.700', _dark: 'gray.300' }}>
                      {doc.fileName}
                    </Text>
                    {doc.isGlobal && (
                      <Badge colorPalette="purple" size="sm" variant="subtle" flexShrink={0}>Global</Badge>
                    )}
                  </HStack>
                );
              })}

              {rootWebSources.map(ws => {
                const isChecked = selectedWebSourceIds.includes(ws.id) || ws.isGlobal;
                return (
                  <HStack
                    key={ws.id}
                    gap={1}
                    px={2}
                    py={1}
                    pl="36px"
                    borderRadius="md"
                    minH="28px"
                    cursor={ws.isGlobal ? 'default' : 'pointer'}
                    _hover={!ws.isGlobal ? { bg: { base: 'gray.50', _dark: 'gray.750' } } : {}}
                    onClick={() => !ws.isGlobal && onToggleWebSource(ws.id)}
                    userSelect="none"
                  >
                    <Box color={isChecked ? (ws.isGlobal ? 'purple.400' : 'blue.500') : { base: 'gray.400', _dark: 'gray.500' }}
                      flexShrink={0}>
                      {isChecked ? <MdCheckBox size={16} /> : <MdCheckBoxOutlineBlank size={16} />}
                    </Box>
                    <Box color={{ base: 'teal.400', _dark: 'teal.300' }} flexShrink={0}>
                      <MdWeb size={14} />
                    </Box>
                    <Text fontSize="xs" flex={1} overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap"
                      color={{ base: 'gray.700', _dark: 'gray.300' }}>
                      {ws.name}
                    </Text>
                    {ws.isGlobal && (
                      <Badge colorPalette="purple" size="sm" variant="subtle" flexShrink={0}>Global</Badge>
                    )}
                  </HStack>
                );
              })}
            </VStack>
          )}
        </Box>
      )}
    </Box>
  );
};

export default KbTreePicker;
